import { useCallback, useState } from 'react';

import api from '~/services/api';
import { useStoreActions } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { normalizeDateString } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';

import { IEditAllocationFormValues } from '../../../shared/model/types';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import {
  buildLocationGroupPayloadForAllocation,
  groupLocationGroupAllocationsToAdd,
} from '../../utils';

const useEditLocationGroupAllocationSubmit = () => {
  const { actTeamId, allocationDate, clinicId, locationGroups } = useEditClientAllocationContext();
  const { isGlobalUser, isActTeamMember } = useRole();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useStoreActions(actions => actions.snackbar);

  const submit = useCallback(
    async (values: IEditAllocationFormValues): Promise<boolean> => {
      const {
        changedLocationGroupAllocationsIds,
        deletedLocationGroupAllocationsIds,
        teamMembersAllocations,
      } = values;

      const allocationsForAdd = groupLocationGroupAllocationsToAdd(
        teamMembersAllocations,
        locationGroups,
      );

      // Allocation IDs that must be updated (PUT) because new groups are being added
      // to members who already have an existing allocation
      const newGroupAllocationIds = allocationsForAdd.reduce<number[]>((acc, alloc) => {
        const member = teamMembersAllocations.find(m => m.id === alloc.userId);
        const existingId = member?.locationGroupAllocations.find(lg => lg.allocation !== null)
          ?.allocation?.id;
        if (existingId !== undefined && !acc.includes(existingId)) acc.push(existingId);
        return acc;
      }, []);

      // Members who truly have no existing allocation → POST
      const allocationsForPost = allocationsForAdd.filter(alloc => {
        const member = teamMembersAllocations.find(m => m.id === alloc.userId);
        return !member?.locationGroupAllocations.some(lg => lg.allocation !== null);
      });

      // All allocation IDs that need a PUT (existing changes + IDs from new groups added to existing allocs)
      const allAllocationIdsToUpdate = Array.from(
        new Set([...changedLocationGroupAllocationsIds, ...newGroupAllocationIds]),
      );

      if (
        !allocationsForPost.length &&
        !allAllocationIdsToUpdate.length &&
        !deletedLocationGroupAllocationsIds.length
      ) {
        return false;
      }

      const datePayload = {
        from: normalizeDateString(allocationDate),
        to: normalizeDateString(allocationDate, true),
        includeWeekends: true as const,
      };

      setIsSubmitting(true);

      try {
        if (allocationsForPost.length && !isGlobalUser) {
          await api.clientAllocation.addClientAllocation(
            { clinicId, teamId: actTeamId },
            { ...datePayload, allocations: allocationsForPost },
          );
        }

        if (deletedLocationGroupAllocationsIds.length) {
          await Promise.all(
            deletedLocationGroupAllocationsIds.map(allocationId => {
              if (isGlobalUser) {
                return api.clientAllocation.deleteOwnClientAllocationGU({
                  clinicId,
                  clientAllocationId: String(allocationId),
                });
              }
              if (isActTeamMember) {
                return api.clientAllocation.deleteOwnClientAllocation({
                  clinicId,
                  teamId: actTeamId,
                  clientAllocationId: String(allocationId),
                });
              }
              return api.clientAllocation.deleteClientAllocation({
                clinicId,
                teamId: actTeamId,
                clientAllocationId: String(allocationId),
              });
            }),
          );
        }

        if (allAllocationIdsToUpdate.length) {
          if (isGlobalUser) {
            await Promise.all(
              allAllocationIdsToUpdate.map(allocationId => {
                const payload = buildLocationGroupPayloadForAllocation(
                  teamMembersAllocations,
                  allocationId,
                  locationGroups,
                  true,
                );
                return api.clientAllocation.updateOwnClientAllocationGU(
                  { clinicId, teamId: actTeamId, clientAllocationId: String(allocationId) },
                  { ...datePayload, ...payload } as any,
                );
              }),
            );
          } else if (isActTeamMember) {
            const [allocationId] = allAllocationIdsToUpdate;
            const payload = buildLocationGroupPayloadForAllocation(
              teamMembersAllocations,
              allocationId,
              locationGroups,
              true,
            );
            await api.clientAllocation.updateOwnClientAllocation(
              { clinicId, teamId: actTeamId, clientAllocationId: String(allocationId) },
              { ...datePayload, ...payload } as any,
            );
          } else {
            const allocations = allAllocationIdsToUpdate.map(allocationId => {
              const payload = buildLocationGroupPayloadForAllocation(
                teamMembersAllocations,
                allocationId,
                locationGroups,
                true,
              );
              return { id: String(allocationId), ...payload };
            });
            await api.clientAllocation.updateClientAllocation(
              { clinicId, teamId: actTeamId },
              { allocations },
            );
          }
        }

        return true;
      } catch (error) {
        showError(extractErrorMessage(error));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [actTeamId, allocationDate, clinicId, isGlobalUser, isActTeamMember, locationGroups, showError],
  );

  return { submit, isSubmitting };
};

export default useEditLocationGroupAllocationSubmit;
